import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import DataTable, { Column } from "@/components/DataTable";
import { LoadingState } from "@/components/LoadingState";
import { auditApi } from "@/api/endpoints";
import { useOrgId } from "@/hooks/useOrgId";
import type { AuditLog } from "@/types";

export default function AuditLogPage() {
  const orgId = useOrgId();
  const [rows, setRows] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async () => {
    setLoading(true);
    const data = await auditApi.list(orgId, { page, action: action || undefined });
    setRows(data.items);
    setTotal(data.total);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, [orgId, page, action]);

  const columns: Column<AuditLog>[] = [
    { key: "when", header: "Timestamp", render: (l) => new Date(l.created_at).toLocaleString("en-ZA") },
    { key: "actor", header: "Actor", render: (l) => l.actor_email ?? "(system)" },
    { key: "action", header: "Action", render: (l) => <code className="text-xs">{l.action}</code> },
    { key: "target", header: "Target", render: (l) => `${l.target_type}${l.target_id ? ` · ${l.target_id.slice(0, 8)}` : ""}` },
    {
      key: "detail",
      header: "Detail",
      render: (l) => l.detail ? (
        <pre className="text-xs bg-slate-50 p-2 rounded max-w-xs overflow-x-auto">{JSON.stringify(l.detail, null, 0)}</pre>
      ) : "—",
    },
  ];

  return (
    <AppLayout>
      <PageHeader
        title="Audit log"
        subtitle={`${total} event${total === 1 ? "" : "s"} recorded`}
        actions={
          <input
            placeholder="Filter by action…"
            className="input max-w-xs"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
          />
        }
      />
      {loading ? <LoadingState /> : <DataTable columns={columns} rows={rows} emptyMessage="No audit events" />}
      <div className="flex justify-between items-center mt-4 text-sm text-slate-600">
        <span>Page {page}</span>
        <div className="flex gap-2">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="btn-secondary" type="button">Previous</button>
          <button onClick={() => setPage(page + 1)} disabled={rows.length === 0} className="btn-secondary" type="button">Next</button>
        </div>
      </div>
    </AppLayout>
  );
}
